/**
 * Hide the Qualified chat panel while any blocking overlay is on screen.
 *
 * Qualified mounts `#qualified-multimodal-host` at `z-index: 2147483642`, which
 * is above everything this site puts on the page — the lity modal sits at 9990.
 * There is no z-index we can win with, so the overlays don't fight it: they
 * suppress it for as long as they are open.
 *
 * Covers the four overlays that currently collide with it:
 *   - lity modals        video lightboxes and the region/language selector
 *   - #geolocation-popup the "choose your country" redirect prompt
 *   - the fixed resource/newsletter card that slides in on scroll
 *   - Ketch's consent dialog (#lanyard_root) — the important one, since a chat
 *     panel covering the privacy choices is a compliance problem, not cosmetics
 *
 * Blocking overlays suppress the chat in BOTH floating and docked mode. Docked,
 * the panel has its own column beside the page, but these overlays are
 * position:fixed and so still span the whole viewport including that column —
 * and the chat outranks all of them on z-index, so it would paint on top.
 *
 * Persistent chrome is handled the other way round: it is nudged clear of the
 * column in CSS and both stay on screen. See the two categories documented in
 * styles/components/_third-party-overlays.scss.
 *
 * Toggles `html.mri-overlay-active`; the CSS lives in
 * styles/components/_third-party-overlays.scss.
 *
 * `#qualified-multimodal-host` is an id Qualified generates. If they rename it
 * this stops working silently, so it is worth re-checking when the chat is
 * upgraded.
 */

// The flag goes on <html>, not <body>. Qualified appends #qualified-multimodal-host
// as a direct child of <html>, so it is a *sibling* of <body> — any selector of the
// form `body.something #qualified-multimodal-host` can never match it.
const ROOT_CLASS = 'mri-overlay-active';

function isVisible(el) {
  if (!el) {
    return false;
  }

  const rect = el.getBoundingClientRect();

  if (rect.height < 1 || rect.width < 1) {
    return false;
  }

  const styles = window.getComputedStyle(el);

  return styles.visibility !== 'hidden' && styles.display !== 'none';
}

// Ketch renders its consent dialog as position:fixed children of #lanyard_root.
// Fixed children contribute nothing to their parent's height, so the container
// measures 0px tall even with the banner full on screen — it has to be judged by
// its contents rather than its own box.
function containsVisibleContent(root) {
  if (!root) {
    return false;
  }

  const descendants = root.querySelectorAll('*');

  for (let i = 0; i < descendants.length; i += 1) {
    if (isVisible(descendants[i])) {
      return true;
    }
  }

  return false;
}

// Docking sets the [q-docked] attribute on <html> while the chat is pinned open
// in its own gutter, and removes it when it undocks — including automatically
// below its own 768px breakpoint. Reading the attribute rather than calling
// qualified.docking.isDocked() keeps this working when docking.js has not loaded
// (blocked, or floating mode), which is exactly when the API is not there to
// call; it is also what isDocked() reads internally.
function isChatDocked() {
  return document.documentElement.hasAttribute('q-docked');
}

function anyOverlayVisible() {
  // --- Blocking overlays: suppress in both modes. -------------------------
  //
  // All of these are position:fixed, so they cover the full viewport including
  // the docked column, and every one of them sits below the chat on z-index.
  // Docking changes nothing about that, which is why none of them consult
  // isChatDocked().

  // Full-screen takeover, and active up to 991px while docking starts at 768px,
  // so the two do genuinely overlap between those widths.
  if (document.body.classList.contains('mobile-menu-open')) {
    return true;
  }

  // lity sets .lity-opened on the modal element it appends to <body>.
  if (document.querySelector('.lity.lity-opened')) {
    return true;
  }

  if (document.body.classList.contains('mri-geolocation-open')) {
    return true;
  }

  // Ketch's consent dialog. The important one: a chat panel over the privacy
  // choices is a compliance problem rather than a cosmetic one.
  if (containsVisibleContent(document.getElementById('lanyard_root'))) {
    return true;
  }

  // --- Persistent chrome: suppress only while floating. --------------------
  //
  // The article resource card is not a blocking overlay — it slides in beside
  // the article and does not demand attention. Floating, the chat bubble lands
  // on top of it and one of the two has to give way. Docked, they coexist: the
  // card is nudged clear of the column in CSS, and hiding a docked panel for a
  // card that is not even modal would be the wrong trade.
  if (!isChatDocked() && isVisible(document.querySelector('.article-inline-resource-card'))) {
    return true;
  }

  return false;
}


function syncOverlayState() {
  document.documentElement.classList.toggle(ROOT_CLASS, anyOverlayVisible());
}

function watchForOverlays() {
  syncOverlayState();

  let queued = false;

  const schedule = () => {
    if (queued) {
      return;
    }
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      syncOverlayState();
    });
  };

  // Every one of these overlays arrives by DOM insertion or a class change, so
  // one observer on <body> catches all four without polling.
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
  });

  // Docking/undocking flips an attribute on <html>, which the <body> observer
  // above cannot see. Without this the suppression state goes stale the moment
  // the chat docks or undocks with an overlay already open.
  new MutationObserver(schedule).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['q-docked'],
  });

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchForOverlays);
} else {
  watchForOverlays();
}



// WEBPACK FOOTER //
// ./scripts/autoload/_overlay-chat-suppression.js